import { useState, useEffect } from 'react'
import {
  DollarSign,
  ArrowDownToLine,
  Wallet,
  Users,
  Loader2,
  Copy,
  Check,
  User as UserIcon,
  ChevronLeft,
  Lock,
  Coins
} from 'lucide-react'
import { useUser } from '@/shared/hooks/useUser'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useResources } from '@/shared/hooks/useResources'
import Button from '@/components/ui/Button'
import PullToRefresh from '@/components/ui/PullToRefresh'

export default function Profile() {
  const { telegramId, hapticFeedback, hapticNotification, showPopup, user: telegramUser } = useTelegram()
  const { data: user, isLoading, error, refetch } = useUser(telegramId)
  const { data: resources } = useResources(user?.id)
  const [copiedReferral, setCopiedReferral] = useState(false)
  const [showLoader, setShowLoader] = useState(false)

  // Delay showing loader to prevent flash
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => setShowLoader(true), 200)
      return () => clearTimeout(timer)
    } else {
      setShowLoader(false)
    }
  }, [isLoading])

  // Get user display name from Telegram
  const getUserDisplayName = () => {
    if (telegramUser?.first_name) {
      return telegramUser.last_name
        ? `${telegramUser.first_name} ${telegramUser.last_name}`
        : telegramUser.first_name
    }
    return 'Профиль'
  }

  const handleCopyReferralCode = async () => {
    if (!user?.referral_code) return

    try {
      await navigator.clipboard.writeText(user.referral_code)
      setCopiedReferral(true)
      hapticNotification('success')
      setTimeout(() => setCopiedReferral(false), 2000)
    } catch (err) {
      console.error('Failed to copy referral code:', err)
      hapticNotification('error')
    }
  }

  const handleComingSoon = async (actionName: string) => {
    hapticFeedback('light')
    await showPopup({
      title: 'Скоро',
      message: `${actionName} будет доступно в следующих обновлениях.`,
      buttons: [{ id: 'ok', type: 'ok', text: 'Понятно' }]
    })
  }

  const handleRefresh = async () => {
    try {
      await refetch()
      hapticNotification?.('success')
    } catch (error) {
      console.error('Failed to refresh user data:', error)
      hapticNotification?.('error')
    }
  }

  if ((isLoading || !user) && showLoader) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  if (isLoading || !user) {
    return null
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-semibold text-tg-text mb-2">Ошибка загрузки</h2>
          <p className="text-tg-hint mb-4">Не удалось загрузить данные профиля</p>
        </div>
      </div>
    )
  }

  const EXCHANGE_RATE = 84.75

  const actionItems = [
    {
      id: 'deposit',
      icon: Wallet,
      title: 'Пополнение',
      description: 'Пополнить баланс',
      color: '#3b82f6',
      bgColor: 'bg-blue-500/20',
      action: () => handleComingSoon('Пополнение баланса'),
    },
    {
      id: 'withdraw',
      icon: ArrowDownToLine,
      title: 'Вывод средств',
      description: 'Вывести на карту',
      color: '#ef4444',
      bgColor: 'bg-red-500/20',
      action: () => handleComingSoon('Вывод средств'),
    },
    {
      id: 'referral',
      icon: Users,
      title: 'Реферальная программа',
      description: 'Пригласить друзей и получить бонус',
      color: '#ec4899',
      bgColor: 'bg-pink-500/20',
      action: () => handleComingSoon('Реферальная программа'),
    },
  ]

  return (
    <div className="min-h-screen bg-tg-bg pb-24">
      <PullToRefresh onRefresh={handleRefresh}>
        <div className="pt-[96px]">
          {/* Header */}
          <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
            <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
            <div className="flex items-center gap-4">
              {/* User Avatar */}
              <div className="relative w-16 h-16 rounded-2xl overflow-hidden flex items-center justify-center bg-tg-button shadow-lg shadow-tg-button/25">
                <UserIcon className="w-8 h-8 text-tg-button-text" />
              </div>

              {/* User Info */}
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-tg-text">{getUserDisplayName()}</h1>
                <p className="text-sm text-tg-hint font-mono">ID: {user.telegram_id}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-3 py-4 space-y-3">
        {/* Balance Cards */}
        <div className="grid grid-cols-2 gap-3">
          {/* Total Balance */}
          <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#10b981]/20">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-[#10b981]/10 rounded-lg flex items-center justify-center">
                <Wallet className="w-5 h-5 text-[#10b981]" />
              </div>
              <span className="text-xs text-tg-hint">Баланс USD</span>
            </div>
            <div className="text-xl font-bold text-tg-text">{user.balance.toFixed(2)}</div>
            <div className="text-xs text-tg-hint mt-0.5">Общая сумма</div>
          </div>

          {/* Available Balance */}
          <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#3b82f6]/20">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-[#3b82f6]/10 rounded-lg flex items-center justify-center">
                <Coins className="w-5 h-5 text-[#3b82f6]" />
              </div>
              <span className="text-xs text-tg-hint">Доступно</span>
            </div>
            <div className="text-xl font-bold text-tg-text">
              {(() => {
                const totalHoldingFees = resources?.reduce((sum, resource) => sum + (resource.holding_fee_amount || 0), 0) || 0
                const resourceRetention = user.resource_retention || 0
                return (user.balance - totalHoldingFees - resourceRetention).toFixed(2)
              })()}
            </div>
            <div className="text-xs text-tg-hint mt-0.5">Можно использовать</div>
          </div>

          {/* Resource Retention */}
          <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#f97316]/20">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-[#f97316]/10 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-[#f97316]" />
              </div>
              <span className="text-xs text-tg-hint">Удержание ресурсов</span>
            </div>
            <div className="text-xl font-bold text-tg-text">
              {(() => {
                const totalHoldingFees = resources?.reduce((sum, resource) => sum + (resource.holding_fee_amount || 0), 0) || 0
                const resourceRetention = user.resource_retention || 0
                return (totalHoldingFees + resourceRetention).toFixed(2)
              })()}
            </div>
            <div className="text-xs text-tg-hint mt-0.5">Замороженные средства</div>
          </div>

          {/* Bot Retention Balance */}
          <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#f59e0b]/20">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-[#f59e0b]/10 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#f59e0b]" />
              </div>
              <span className="text-xs text-tg-hint">Удержание бота</span>
            </div>
            <div className="text-xl font-bold text-tg-text">{user.retention_balance.toFixed(2)}</div>
            <div className="text-xs text-tg-hint mt-0.5">Ожидает выплаты</div>
          </div>
        </div>

        {/* Exchange Rate */}
        <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-tg-hint mb-1">Курс обмена</p>
              <p className="text-xl font-bold text-tg-text">{EXCHANGE_RATE} ₽</p>
            </div>
            <div className="w-10 h-10 bg-tg-hint/5 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-tg-hint/30" />
            </div>
          </div>
        </div>

        {/* Referral Code */}
        {user.referral_code && (
          <div className="bg-tg-secondary-bg rounded-2xl p-3 shadow-sm border-2 border-[#ec4899]/20">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-[#ec4899]/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-[#ec4899]" />
              </div>
              <span className="text-xs text-tg-hint">Реферальный код</span>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-tg-bg px-3 py-2 rounded-lg text-tg-text font-mono text-sm">
                {user.referral_code}
              </code>
              <Button
                onClick={handleCopyReferralCode}
                variant="secondary"
                className="!px-3"
              >
                {copiedReferral ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Actions Menu */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm border border-tg-secondary-bg overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <h2 className="font-semibold text-tg-text">Действия</h2>
          </div>

          {actionItems.map((item, index) => {
            const Icon = item.icon
            const isLast = index === actionItems.length - 1

            return (
              <button
                key={item.id}
                onClick={() => {
                  hapticFeedback('light')
                  item.action()
                }}
                className={`w-full flex items-center justify-between p-4 hover:bg-tg-hint/10 active:bg-tg-hint/20 transition-colors ${!isLast ? 'border-b border-tg-secondary-bg' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.bgColor}`}>
                    <Icon className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-tg-text">{item.title}</div>
                    <div className="text-xs text-tg-hint">{item.description}</div>
                  </div>
                </div>
                <ChevronLeft className="w-5 h-5 text-tg-hint/50 rotate-180" />
              </button>
            )
          })}
        </div>
        </div>
        </div>
      </PullToRefresh>
    </div>
  )
}
